import React from 'react';
import { Alert, Button } from 'antd';
import { SynonymError, SynonymWarning } from '../../../lib/api/searchDictionary';
import { formatTerms } from '../utils/terms';

type RuleFeedbackProps = {
  errors: SynonymError[];
  warnings: SynonymWarning[];
  /** 409 — 지금 저장된 토큰 */
  conflict: string[] | null;
  message: string | null;
  onReload?: () => void;
  /** 겹치는 줄 바로가기 — 없으면 id 만 보여 준다 */
  onClickRule?: (ruleId: string) => void;
  /** 모달처럼 좁은 곳에선 경고를 한 줄로 줄인다 */
  compact?: boolean;
};

/**
 * 저장 차단(오류) · 확인 후 저장(경고) · 충돌 안내 (S2).
 * 경고는 막지 않는다 — 지금 사전에 겹치는 토큰이 많아 막으면 기존 줄을 고치는 것부터 막힌다.
 */
export const RuleFeedback = (props: RuleFeedbackProps): React.JSX.Element | null => {
  const { errors, warnings, conflict, message, onReload, onClickRule, compact } = props;
  if (!errors.length && !warnings.length && !conflict && !message) return null;

  return (
    <div className="rule-feedback">
      {conflict && (
        <Alert
          type="error"
          showIcon
          message={message ?? '그 사이 다른 사람이 이 줄을 바꿨습니다.'}
          description={conflict.length ? `지금 저장된 값: ${formatTerms(conflict)}` : undefined}
          action={
            onReload && (
              <Button size="small" onClick={onReload}>
                새로 불러오기
              </Button>
            )
          }
        />
      )}
      {!conflict && message && !errors.length && <Alert type="error" showIcon message={message} />}
      {errors.map((error, index) => (
        <Alert key={`e-${index}`} type="error" showIcon message={error.message} />
      ))}
      {compact && warnings.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          message={`확인할 것 ${warnings.length}건`}
          description={warnings.map((warning) => warning.message).join(' ')}
        />
      ) : (
        warnings.map((warning, index) => (
          <Alert
            key={`w-${index}`}
            type="warning"
            showIcon
            message={
              <>
                {warning.message}
                {warning.relatedRuleIds && warning.relatedRuleIds.length > 0 && (
                  <span className="related-rules">
                    {warning.relatedRuleIds.map((ruleId) =>
                      onClickRule ? (
                        <Button key={ruleId} type="link" size="small" onClick={() => onClickRule(ruleId)}>
                          겹치는 줄 보기
                        </Button>
                      ) : (
                        <span key={ruleId} className="rule-id">
                          {ruleId}
                        </span>
                      ),
                    )}
                  </span>
                )}
              </>
            }
          />
        ))
      )}
    </div>
  );
};
